/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type { ApiWithQuery } from "@intuitionrobotics/thunderstorm";
import type {DB_Object} from "@intuitionrobotics/ts-common";

export type DB_Organization = DB_Object & {
	name: string
	hideDisenrolledCustomers?: boolean
	groupContactIds: string[]
	key: string
	clinical?: boolean
	type: ORGANIZATION_TYPE
	/**
	 * Pointer to the org's logo in care-center's Cloud Storage bucket — never the bytes
	 * themselves. `/v1/organization/query` is fetched on every page load for every org a user
	 * can see, so the doc carries only this, and the presence of the field is what tells a UI
	 * whether to look for a logo at all instead of drawing initials.
	 *
	 * Care-center is the only writer; anything reading `DB_Organization` gets it for free.
	 */
	logo?: OrganizationLogo
}

/**
 * The stored shape of an org logo. Lives here rather than in a care-center-local extension
 * type because `DB_Organization` is the org type every Q app reads — a parallel type that
 * shadowed it would be the more expensive mistake.
 */
export type OrganizationLogo = {
	/** Object path inside care-center's default bucket. */
	path: string
	contentType: OrganizationLogoContentType
	/** Bytes, as reported by the bucket at commit time. */
	size: number
	updated: number
	updatedBy: string
	/**
	 * The file the user actually picked, archived unmodified. The displayed logo above is a
	 * <=256px raster the browser derives from it, so without this the source is gone the moment
	 * it is uploaded — and a logo that later has to be shown large, or printed, could not be
	 * regenerated without asking every org to upload again.
	 *
	 * Deliberately never rendered: it is not served to a browser, has no read URL, and is
	 * stored `Content-Disposition: attachment`. That, not any validation of its contents, is
	 * why it is safe for this to be an SVG.
	 *
	 * Absent on logos uploaded before this existed; present on every new one.
	 */
	original?: OrganizationLogoOriginal
}

export type OrganizationLogoOriginal = {
	path: string
	contentType: OrganizationLogoOriginalContentType
	size: number
}

/**
 * Raster only. GCS never sniffs an upload, so the stored content type is whatever the PUT
 * declared; pinning it to this set is what stops a logo ever being served back as HTML or as
 * an SVG, and care-center verifies the bytes' magic number against it on commit. The uploader
 * rasterizes whatever the user picks (SVG included) into one of these in the browser.
 */
export const OrganizationLogoContentTypes = ["image/png", "image/jpeg", "image/webp"] as const;
export type OrganizationLogoContentType = typeof OrganizationLogoContentTypes[number];

export const isOrganizationLogoContentType = (contentType?: string): contentType is OrganizationLogoContentType =>
	!!contentType && (OrganizationLogoContentTypes as readonly string[]).includes(contentType);

/**
 * What may be archived as the original. SVG is allowed here and nowhere else, precisely
 * because the original is never rendered. The set is otherwise bounded by what a browser can
 * rasterize — anything it cannot decode has no displayable logo to derive, so accepting it
 * would archive a source with nothing to show for it.
 */
export const OrganizationLogoOriginalContentTypes = [...OrganizationLogoContentTypes, "image/svg+xml"] as const;
export type OrganizationLogoOriginalContentType = typeof OrganizationLogoOriginalContentTypes[number];

export const isOrganizationLogoOriginalContentType = (contentType?: string): contentType is OrganizationLogoOriginalContentType =>
	!!contentType && (OrganizationLogoOriginalContentTypes as readonly string[]).includes(contentType);

export type CreateOrganizationRequest = {
	organization: Omit<DB_Organization, keyof DB_Object>
	mainContactName?: string      // For Clinical contact (index 1)
	secondContactName?: string    // For External contact (index 2)
}

export enum ORGANIZATION_TYPE {
	INTERNAL = "Internal",
	CLINICAL = "Clinical",
	EXTERNAL = "External",
	MULTI_CONTACT = "MultiContact"
}

export type ApiListOrganizations = ApiWithQuery<'/v1/organization/list', DB_Organization[]>
